import { createContext, useEffect, useMemo } from "react";
import { capitalCase } from "change-case";
import { useSearchParams } from "react-router-dom";
// firestore
import { onAuthStateChanged, signInWithCustomToken, signOut } from "firebase/auth";
import { auth } from "../services/firebase";
// utils
import axios from "../utils/axios";
// hooks
import useCustomReducer from "../hooks/useCustomReducer";
// auth
import reducer from "../reducers/authReducer";
import actionTypes from "../common/actionTypes";

// auth constants
const SHOPIFY_DOMAIN = ".myshopify.com";
const FIREBASE_AUTH_URL = "/auth/getCustomToken";

const initialState = {
  loading: false,
  error: null,
  user: null,
};

const AuthContext = createContext({
  ...initialState,
});

const AuthProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(reducer, initialState);

  const [searchParams] = useSearchParams();
  const shopifySessionToken = searchParams.get("id_token");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken(true);

        if (token) {
          dispatch({
            type: actionTypes.SET_USER_TO_STORE,
            payload: {
              user: {
                name: capitalCase(user.uid.split(SHOPIFY_DOMAIN)[0].replaceAll("-", " ")),
                domain: user.uid,
              },
            },
          });
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      if (shopifySessionToken) {
        try {
          const authResponse = await axios.get(FIREBASE_AUTH_URL, {
            headers: {
              Authorization: shopifySessionToken,
            },
          });
          const token = authResponse?.data?.data ? authResponse.data.data.token : authResponse.data.token;

          if (token) {
            signInWithCustomToken(auth, token);
          }
        } catch (error) {
          dispatch({
            type: actionTypes.LOGIN_FAILURE,
            payload: error,
          });

          if (auth.currentUser?.uid) {
            signOut(auth);

            localStorage.clear();

            dispatch({ type: actionTypes.LOGOUT });
          }
        }
      }
    })();
  }, [dispatch, shopifySessionToken]);

  const memoizedState = useMemo(() => state, [state]);

  return <AuthContext.Provider value={memoizedState}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
